import React, { useState } from 'react';
import {
  TrashIcon,
  MinusIcon,
  PlusIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

/**
 * OrderCart Component
 * Displays items in the current order/cart
 */
export const OrderCart = ({
  items,
  onUpdateQuantity,
  onRemoveItem,
  onUpdateNotes,
  onClearCart,
  loading,
  adminMode
}) => {
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemNotes, setItemNotes] = useState({});

  const handleAddQuantity = (productId) => {
    const item = items.find(i => i.productId === productId);
    onUpdateQuantity(productId, (item?.quantity || 0) + 1);
  };

  const handleReduceQuantity = (productId) => {
    const item = items.find(i => i.productId === productId);
    const newQty = (item?.quantity || 1) - 1;
    if (newQty > 0) {
      onUpdateQuantity(productId, newQty);
    } else {
      onRemoveItem(productId);
    }
  };

  const handleSaveNotes = (productId) => {
    onUpdateNotes(productId, itemNotes[productId] || '');
    setEditingItemId(null);
  };

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-white">
        <div className="text-center text-gray-500">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
            />
          </svg>
          <p className="text-lg font-medium">Cart is Empty</p>
          <p className="text-sm mt-1">Select products to add items</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Cart Header */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-900">
          Order Items ({items.length})
        </h2>
        {items.length > 0 && (
          <button
            onClick={onClearCart}
            disabled={loading}
            className="text-sm px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded transition disabled:opacity-50"
          >
            Clear
          </button>
        )}
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {items.map((item) => (
          <div key={item.productId} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            {/* Item Header */}
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-sm line-clamp-2">
                  {item.productName}
                </h3>
                {item.productSku && (
                  <p className="text-xs text-gray-500 mt-0.5">{item.productSku}</p>
                )}
              </div>
              <button
                onClick={() => onRemoveItem(item.productId)}
                disabled={loading}
                className="text-red-500 hover:text-red-700 p-1 transition disabled:opacity-50"
                title="Remove item"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Quantity Controls */}
            <div className="flex items-center justify-between mb-2 bg-white rounded border border-gray-300 p-2">
              <button
                onClick={() => handleReduceQuantity(item.productId)}
                disabled={loading}
                className="text-gray-600 hover:text-gray-900 transition disabled:opacity-50"
              >
                <MinusIcon className="h-4 w-4" />
              </button>
              <input
                type="number"
                min="1"
                step="1"
                value={Math.round(item.quantity)}
                onChange={(e) => {
                  const qty = parseInt(e.target.value) || 1;
                  onUpdateQuantity(item.productId, qty);
                }}
                className="w-12 text-center text-sm font-semibold border-none bg-white py-0"
              />
              <button
                onClick={() => handleAddQuantity(item.productId)}
                disabled={loading}
                className="text-gray-600 hover:text-gray-900 transition disabled:opacity-50"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
              <span className="ml-2 text-sm text-gray-600">
                ₱{(item.price * item.quantity).toFixed(2)}
              </span>
            </div>

            {/* Price Breakdown */}
            <div className="flex justify-between items-center text-xs text-gray-600 mb-2">
              <span>₱{item.price.toFixed(2)} each</span>
              <span>{item.quantity} × ₱{item.price.toFixed(2)}</span>
            </div>

            {/* Notes Section */}
            <div className="mt-2">
              {editingItemId === item.productId ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={itemNotes[item.productId] || item.notes || ''}
                    onChange={(e) => setItemNotes({ ...itemNotes, [item.productId]: e.target.value })}
                    placeholder="Special requests..."
                    className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded"
                    autoFocus
                  />
                  <button
                    onClick={() => handleSaveNotes(item.productId)}
                    className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Save
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => {
                    setEditingItemId(item.productId);
                    setItemNotes({ ...itemNotes, [item.productId]: item.notes || '' });
                  }}
                  className="bg-white border border-gray-300 rounded px-2 py-1 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="text-xs text-gray-600 line-clamp-1">
                    {item.notes ? `Notes: ${item.notes}` : 'Click to add notes...'}
                  </span>
                  <PencilIcon className="h-3 w-3 text-gray-400 ml-1" />
                </div>
              )}
            </div>

            {/* Admin Controls */}
            {adminMode && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <button
                  onClick={() => onRemoveItem(item.productId)}
                  disabled={loading}
                  className="w-full text-xs px-2 py-1 bg-red-100 text-red-600 hover:bg-red-200 rounded transition disabled:opacity-50"
                >
                  Remove (Admin)
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderCart;
